import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { signInWithOAuth, type OAuthProvider } from '../services/auth';
import { useFavoritesStore } from './favoritesStore';

// 사용자 역할 타입
// - 'user': 일반 회원 (수강 신청 등)
// - 'provider': 교육 기관 (수업/인증서 발급, UI 라벨은 "교육 기관")
// - 'employer': 기업 회원 (구인 공고/이력서 열람)
// - 'admin': 관리자 (모든 기능)
export type UserRole = 'user' | 'provider' | 'employer' | 'admin';

// 커스텀 유저 타입 (Supabase Auth 유저 + 네이버 OAuth 유저)
export interface AppUser {
  id: string;
  email: string;
  name?: string;
  nickname?: string;
  avatar_url?: string;
  provider: 'naver' | 'kakao' | 'google' | 'email';
  role: UserRole; // 사용자 역할
  created_at?: string;
  terms_agreed?: boolean;
  is_premium?: boolean; // 인증 우수 상태
  banner_image_url?: string | null; // 우수 인증 교육 기관 배너 이미지
  short_id?: string; // 개인 고유번호 (6자리)

  // 프로바이더 정보 (user_metadata)
  description?: string; // 업체 소개
  phone?: string; // 센터 전화번호
  website?: string; // 홈페이지
  address?: string; // 주소
  operating_hours?: string; // 운영 시간
  // 개인 정보
  birth_date?: string; // 생년월일 (YYYY-MM-DD)
  name_en?: string; // 영문 이름
}

interface AuthStore {
  user: AppUser | null;
  loading: boolean;
  error: Error | null;
  isAuthenticated: boolean;

  // Computed helpers
  isAdmin: () => boolean;
  isProvider: () => boolean;
  isEmployer: () => boolean;

  // Actions
  initialize: () => Promise<void>;
  signIn: (provider: OAuthProvider) => Promise<void>;
  signOut: () => Promise<void>;
  setUser: (user: AppUser | null) => void;
  loginSuccess: (userId: string, provider: string, isNew: boolean, name?: string, email?: string) => void;
  checkSession: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      loading: false,
      error: null,
      isAuthenticated: false,

      // 역할 확인 헬퍼
      isAdmin: () => get().user?.role === 'admin',
      isProvider: () => get().user?.role === 'provider',
      isEmployer: () => get().user?.role === 'employer',

      initialize: async () => {
        // 앱 시작 시 서버 세션 확인
        await get().checkSession();
      },

      checkSession: async () => {
        try {
          console.log('checkSession: fetching /api/v1/auth/me');
          const response = await fetch('/api/v1/auth/me', {
            credentials: 'include', // 쿠키 포함
          });

          const data = await response.json();
          console.log('checkSession: response data', data);

          if (data.authenticated && data.user) {
            console.log('checkSession: setting authenticated user', data.user);
            set({
              user: {
                id: data.user.id,
                email: data.user.email,
                name: data.user.name,
                avatar_url: data.user.avatar_url,
                provider: data.user.provider,
                role: data.user.role || 'user', // 역할 (기본값: user)
                terms_agreed: data.user.terms_agreed,
                is_premium: data.user.is_premium, // 인증 우수 상태
                banner_image_url: data.user.banner_image_url, // 배너 이미지
                short_id: data.user.short_id, // 개인 고유번호
                // 프로바이더 정보
                description: data.user.description,
                phone: data.user.phone,
                website: data.user.website,
                address: data.user.address,
                operating_hours: data.user.operating_hours,
                // 개인 정보
                birth_date: data.user.birth_date,
                name_en: data.user.name_en,
              },
              isAuthenticated: true,
              loading: false,
            });
            return;
          }

          // 세션 없으면 로컬 상태도 클리어
          const localUser = get().user;
          if (localUser) {
            // 로컬에 유저 있는데 서버 세션 없으면 클리어
            set({
              user: null,
              isAuthenticated: false,
              loading: false,
            });
          } else {
            set({ loading: false });
          }
        } catch (err) {
          console.error('Session check error:', err);
          set({ loading: false });
        }
      },

      signIn: async (provider: OAuthProvider) => {
        set({ loading: true, error: null });
        try {
          await signInWithOAuth(provider);
          // OAuth 리다이렉트 후 처리됨
        } catch (error) {
          set({
            loading: false,
            error: error as Error,
          });
          throw error;
        }
      },

      signOut: async () => {
        set({ loading: true, error: null });
        try {
          // 서버 세션 클리어
          await fetch('/api/v1/auth/logout', {
            method: 'POST',
            credentials: 'include',
          });

          // 로컬 상태 클리어
          set({
            user: null,
            loading: false,
            isAuthenticated: false,
          });
          useFavoritesStore.getState().clear();
        } catch (error) {
          set({
            loading: false,
            error: error as Error,
          });
          throw error;
        }
      },

      setUser: (user: AppUser | null) => {
        set({
          user,
          isAuthenticated: !!user,
        });
      },

      loginSuccess: (userId: string, provider: string, isNew: boolean, name?: string, email?: string) => {
        // OAuth 로그인 성공 시 호출 (URL 파라미터에서)
        // 역할은 checkSession에서 서버로부터 받아옴
        const user: AppUser = {
          id: userId,
          email: email || '',
          name: name || undefined,
          provider: provider as AppUser['provider'],
          role: 'user', // 기본값, checkSession에서 업데이트됨
        };
        set({
          user,
          isAuthenticated: true,
          loading: false,
        });
      },
    }),
    {
      name: 'stampfit-auth',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);
